/* eslint-disable no-bitwise */
/* eslint-disable no-restricted-syntax */

import XLSX from 'xlsx';
import dayjs from 'dayjs';
import { TIME_FORMAT_UTC_OFFSET_TIMEZONE } from '../dates';

class XLSXDailyConsumptionStrategy {
  constructor(metric) {
    this.massRatio = metric ? 0.001 : 0.00220462;
    this.massUnit = metric ? 'kg' : 'lbs';
    this.timeZoneString = dayjs.tz().format(TIME_FORMAT_UTC_OFFSET_TIMEZONE);
    this.sheetRows = [];
    this.roomNames = [];
    this.feedlineNames = [];
  }

  // eslint-disable-next-line no-unused-vars
  setFarmObjects = () => {
    // Not needed for columnar format, but keeping for interface compatibility
  };

  processDailyConsumption = (data) => {
    const { columnarData, roomNames, feedlineNames } = data;
    this.roomNames = roomNames;
    this.feedlineNames = feedlineNames;

    // Build header row
    const header = [`Date (${this.timeZoneString})`, 'Barn Name', `Barn (${this.massUnit})`, `Barn Per Animal (${this.massUnit})`];

    // Add room columns (name and per animal for each room)
    roomNames.forEach((roomName) => {
      header.push(`RM: ${roomName} (${this.massUnit})`);
      header.push(`RM: ${roomName} Per Animal (${this.massUnit})`);
    });

    // Add feedline columns (only total consumption, no per-animal)
    feedlineNames.forEach((feedlineName) => {
      header.push(`FL: ${feedlineName} (${this.massUnit})`);
    });

    this.sheetRows.push(header);

    // Build data rows
    columnarData.forEach((rowData) => {
      const dateObj = new Date(rowData.date * 1000);
      const row = [
        dateObj,
        rowData.barnName || '',
        rowData.wholeBarn != null ? rowData.wholeBarn * this.massRatio : null,
        rowData.wholeBarnPerAnimal != null ? rowData.wholeBarnPerAnimal * this.massRatio : null,
      ];

      // Add room data
      rowData.rooms.forEach((room) => {
        row.push(room.total != null ? room.total * this.massRatio : null);
        row.push(room.perAnimal != null ? room.perAnimal * this.massRatio : null);
      });

      // Add feedline data (only total consumption)
      rowData.feedlines.forEach((feedline) => {
        row.push(feedline.total != null ? feedline.total * this.massRatio : null);
      });

      this.sheetRows.push(row);
    });
  };

  // Helper method to create sheet data with proper formatting
  createSheetData = (sheetRows) => {
    const sheetData = {};
    const range = { s: { r: 0, c: 0 }, e: { r: sheetRows.length - 1, c: sheetRows[0].length - 1 } };
    sheetData['!ref'] = XLSX.utils.encode_range(range);

    // Add header row
    sheetRows[0].forEach((cell, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      sheetData[cellAddress] = { v: cell };
    });

    // Add data rows with proper date and number formatting
    const dateFormat = 'yyyy-mm-dd';
    const numberFormat = '#,##0.00'; // Format for consumption values

    for (let row = 1; row < sheetRows.length; row++) {
      sheetRows[row].forEach((cell, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: colIndex });

        if (colIndex === 0 && cell instanceof Date) {
          // Format date column
          sheetData[cellAddress] = {
            v: cell,
            t: 'd',
            z: dateFormat,
          };
        } else if (colIndex >= 2 && typeof cell === 'number') {
          // Format all numeric consumption columns (starting from column 2)
          sheetData[cellAddress] = {
            v: cell,
            t: 'n',
            z: numberFormat,
          };
        } else if (cell === null || cell === undefined) {
          // Empty cells
          sheetData[cellAddress] = { t: 'e', v: 0x2a };
        } else {
          // Regular cells
          sheetData[cellAddress] = { v: cell };
        }
      });
    }

    // Set column widths dynamically
    const columnWidths = [
      { wch: 20 }, // Date
      { wch: 20 }, // Barn Name
      { wch: 15 }, // Barn
      { wch: 20 }, // Barn Per Animal
    ];

    // Add widths for room columns (2 columns per room)
    this.roomNames.forEach(() => {
      columnWidths.push({ wch: 15 }); // Room total
      columnWidths.push({ wch: 20 }); // Room per animal
    });

    // Add widths for feedline columns (1 column per feedline)
    this.feedlineNames.forEach(() => {
      columnWidths.push({ wch: 15 }); // Feedline total
    });

    sheetData['!cols'] = columnWidths;

    return sheetData;
  };

  createFile = () => {
    const s2ab = (s) => {
      if (typeof ArrayBuffer !== 'undefined') {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i !== s.length; i += 1) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
      }
      const buf = new Array(s.length);
      for (let i = 0; i !== s.length; i += 1) buf[i] = s.charCodeAt(i) & 0xff;
      return buf;
    };

    const workbook = XLSX.utils.book_new();
    const sheetData = this.createSheetData(this.sheetRows);
    XLSX.utils.book_append_sheet(workbook, sheetData, 'Sheet 1');

    const wopts = { bookType: 'xlsx', bookSST: false, type: 'binary' };
    const wbout = XLSX.write(workbook, wopts);
    return new Blob([s2ab(wbout)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };
}

export default XLSXDailyConsumptionStrategy;
